import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { useState, useMemo } from 'react';

// EU country codes (SEPA countries)
const EU_COUNTRIES = [
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'MC', 'NL', 'PT', 'SM', 'SK', 'SI', 'AD', 'VA'
];

interface WalletCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (chain: string, asset: string) => void;
  countryCode?: string | null;
  isCreating?: boolean;
}

const AVAILABLE_CHAINS = [
  { value: 'base', label: 'Base', description: 'USDC native, low fees' },
  { value: 'ethereum', label: 'Ethereum', description: 'Most widely supported' },
  { value: 'solana', label: 'Solana', description: 'Fast and low cost' },
];

const AVAILABLE_ASSETS = [
  { value: 'USDC', label: 'USDC', description: 'USD Coin - Most widely accepted' },
  { value: 'USDT', label: 'USDT', description: 'Tether - Not available in EU' },
];

export function WalletCreationModal({
  open,
  onClose,
  onSuccess,
  countryCode,
  isCreating = false,
}: WalletCreationModalProps) {
  const [selectedChain, setSelectedChain] = useState('base');
  const [selectedAsset, setSelectedAsset] = useState('USDC');

  // Filter available assets based on country code
  const availableAssets = useMemo(() => {
    const isEU = countryCode && EU_COUNTRIES.includes(countryCode.toUpperCase());
    
    if (isEU) {
      // EU users: USDC only (USDT not available)
      return AVAILABLE_ASSETS.filter(asset => asset.value === 'USDC');
    }
    
    // Non-EU users: All assets
    return AVAILABLE_ASSETS;
  }, [countryCode]);

  // Auto-select USDC if USDT becomes unavailable
  useMemo(() => {
    if (selectedAsset === 'USDT' && !availableAssets.find(a => a.value === 'USDT')) {
      setSelectedAsset('USDC');
    }
  }, [availableAssets, selectedAsset]);

  const isEU = countryCode && EU_COUNTRIES.includes(countryCode.toUpperCase());
  const canCreate = selectedChain && selectedAsset && !isCreating;

  const handleCreate = () => {
    if (!canCreate) return;
    onSuccess(selectedChain, selectedAsset);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className={cn("sm:max-w-[500px]", semanticTokens.motion.standard)}
        description="Choose your preferred network and stablecoin for your wallet"
      >
        <DialogHeader>
          <DialogTitle className={semanticTokens.typography.h3}>
            Create Your Wallet
          </DialogTitle>
          <DialogDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            Select a network and stablecoin to create your Supabank wallet. Your funds will be secure and insured.
          </DialogDescription>
        </DialogHeader>

        <div className={cn("space-y-6", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
          {/* Network Selection */}
          <div className={cn("space-y-3", semanticTokens.spacing.sm.replace('p-', 'space-y-'))}>
            <Label className={cn(semanticTokens.typography.label)}>
              Network
            </Label>
            <RadioGroup
              value={selectedChain}
              onValueChange={setSelectedChain}
              className="grid grid-cols-1 gap-3"
            >
              {AVAILABLE_CHAINS.map((chain) => (
                <div
                  key={chain.value}
                  className={cn(
                    "flex items-start space-x-3 rounded-lg border p-4",
                    semanticTokens.border.default,
                    selectedChain === chain.value && "border-primary bg-primary/5",
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                >
                  <RadioGroupItem
                    value={chain.value}
                    id={`chain-${chain.value}`}
                    className="mt-1"
                    aria-label={`Select ${chain.label} network`}
                  />
                  <label
                    htmlFor={`chain-${chain.value}`}
                    className={cn(
                      "flex-1 cursor-pointer space-y-1",
                      semanticTokens.typography.body
                    )}
                  >
                    <div className={cn("font-medium", semanticTokens.fg.default)}>
                      {chain.label}
                    </div>
                    <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      {chain.description}
                    </div>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Asset Selection */}
          <div className={cn("space-y-3", semanticTokens.spacing.sm.replace('p-', 'space-y-'))}>
            <Label className={cn(semanticTokens.typography.label)}>
              Stablecoin
            </Label>
            <RadioGroup
              value={selectedAsset}
              onValueChange={setSelectedAsset}
              className="grid grid-cols-1 gap-3"
            >
              {availableAssets.map((asset) => (
                <div
                  key={asset.value}
                  className={cn(
                    "flex items-start space-x-3 rounded-lg border p-4",
                    semanticTokens.border.default,
                    selectedAsset === asset.value && "border-primary bg-primary/5",
                    semanticTokens.motion.standard,
                    semanticTokens.focus.ring
                  )}
                >
                  <RadioGroupItem
                    value={asset.value}
                    id={`asset-${asset.value}`}
                    className="mt-1"
                    aria-label={`Select ${asset.label} stablecoin`}
                  />
                  <label
                    htmlFor={`asset-${asset.value}`}
                    className={cn(
                      "flex-1 cursor-pointer space-y-1",
                      semanticTokens.typography.body
                    )}
                  >
                    <div className={cn("font-medium", semanticTokens.fg.default)}>
                      {asset.label}
                    </div>
                    <div className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                      {asset.description}
                    </div>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* EU Notice */}
          {isEU && (
            <Alert className={cn(semanticTokens.info.bg, semanticTokens.info.border)}>
              <SFIcon icon={sfIcons.info} className={cn("w-4 h-4", semanticTokens.info.accent)} aria-hidden="true" />
              <AlertDescription className={cn(semanticTokens.typography.body, semanticTokens.info.fg)}>
                USDT is not available in your region. USDC is available on all supported networks.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className={cn("gap-2", semanticTokens.spacing.sm.replace('p-', 'gap-'))}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreating}
            className={cn("min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            aria-label="Cancel wallet creation"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate}
            className={cn("min-h-[44px]", semanticTokens.motion.standard, semanticTokens.focus.ring)}
            aria-label="Create wallet"
          >
            {isCreating ? (
              <>
                <InlineLoadingIndicator size="sm" className="mr-2" aria-hidden="true" />
                Creating...
              </>
            ) : (
              'Create Wallet'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

